import { type TutorialLevel } from "./tutorial-data";

const PAOLO_TUT_LVL_4: TutorialLevel = {
  name: "Advanced Logic",
  description:
    "Learn advanced techniques that will help tackle larger puzzles.",
  gridLayout: [
    [0, 1, 1, 0, 1, 1, 1],
    [1, 0, 0, 1, 1, 0, 1],
    [1, 1, 1, 0, 1, 1, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [1, 0, 0, 0, 0, 0, 1],
    [1, 1, 1, 1, 1, 1, 0],
    [0, 0, 0, 0, 0, 0, 0],
  ],
  phases: [
    {
      waitFor: "message",
      allowGridInput: true,
      message: <p>Now let's look at another puzzle.</p>,
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          There are a few 0's, so just like before, fill those rows with X's.
        </p>
      ),
      vignettes: [
        {
          type: "row",
          index: 3,
        },
        {
          type: "row",
          index: 6,
        },
      ],
      requireX: [
        [0, 3],
        [1, 3],
        [2, 3],
        [3, 3],
        [4, 3],
        [5, 3],
        [6, 3],
        [0, 6],
        [1, 6],
        [2, 6],
        [3, 6],
        [4, 6],
        [5, 6],
        [6, 6],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Now, let's look at the first column. There must be two blocks of two
          shaded squares. There are only two ways for this to happen.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      demoFadeTiles: [
        [
          [0, 0],
          [0, 1],
          [0, 4],
          [0, 5],
        ],
        [
          [0, 1],
          [0, 2],
          [0, 4],
          [0, 5],
        ],
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          Color in all the squares in this column that are guaranteed to be
          shaded.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      requireColor: [
        [0, 1],
        [0, 4],
        [0, 5],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>Great job! Now, look at the row with the 6.</p>,
      vignettes: [
        {
          type: "row",
          index: 5,
        },
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          We have one shaded square at the edge, so the placement of the six
          shaded squares are fixed. Shade in all the remaining squares.
        </p>
      ),
      requireColor: [
        [1, 5],
        [2, 5],
        [3, 5],
        [4, 5],
        [5, 5],
      ],
      vignettes: [
        {
          type: "row",
          index: 5,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>Now, look at the rightmost column.</p>,
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Think about places to place the one shaded square, knowing that the
          column must contain a block of two shaded squares, followed by some
          white space, followed by a single shaded square.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          If you placed the single square here, it'd be impossible to place a
          block of two squares above it with some white space in between.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      highlightRects: [
        {
          start: [6, 2],
          end: [6, 2],
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          There are only two squares above the shaded square, and shading in
          both of them would make a block of three shaded squares.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      demoFadeTiles: [
        [[6, 2]],
        [
          [6, 0],
          [6, 1],
          [6, 2],
        ],
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          Therefore, the shaded square must be in the open space below. Fill it
          in now.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      highlightRects: [{ start: [6, 4], end: [6, 4] }],
      requireColor: [[6, 4]],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          The 2 clue in this column also allows you to know the location of one
          shaded square, using the rules we've learned. Fill in that square.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      requireColor: [[6, 1]],
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <>
          <p>
            Look at the highlighted row. Just like in the previous step, there
            is one square that is guaranteed to be shaded. Fill it in.
          </p>
          <p>
            <i>
              Hint: Placing X's in squares that cannot be shaded in will make
              this easier to visualize.
            </i>
          </p>
        </>
      ),
      vignettes: [
        {
          type: "row",
          index: 1,
        },
      ],
      requireColor: [[3, 1]],
    },
    {
      waitFor: "puzzleComplete",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          Now you have everything you need to finish the puzzle. Do that now.
        </p>
      ),
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>Great job!</p>,
    },
  ],
};

export default PAOLO_TUT_LVL_4;
